import { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminGetHotelReservation,
  adminPreviewHotelReservation,
  adminSendHotelReservation,
  adminUpsertHotelReservation,
  type ConferenceHotelReservationBundle,
  type ConferenceHotelReservationInput,
  type ConferenceHotelEmailPreview,
} from "../api";
import { formatDateTime } from "../format";
import { PageHeader } from "../shells/PageHeader";

const emptyForm = (): ConferenceHotelReservationInput => ({
  hotel_name: "",
  hotel_address: "",
  check_in_date: "",
  check_out_date: "",
  booking_reference: "",
  room_type: "",
  room_number: "",
  contact_phone: "",
  notes: "",
  maps_url: "",
});

export function AdminHotelReservationPage() {
  const { registrationId } = useParams<{ registrationId: string }>();
  const { t, locale } = useLanguage();
  const [bundle, setBundle] = useState<ConferenceHotelReservationBundle | null>(null);
  const [form, setForm] = useState<ConferenceHotelReservationInput>(emptyForm);
  const [preview, setPreview] = useState<ConferenceHotelEmailPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const applyBundle = (b: ConferenceHotelReservationBundle) => {
    setBundle(b);
    const r = b.reservation;
    if (r) {
      setForm({
        hotel_name: r.hotel_name,
        hotel_address: r.hotel_address,
        check_in_date: r.check_in_date,
        check_out_date: r.check_out_date,
        booking_reference: r.booking_reference,
        room_type: r.room_type,
        room_number: r.room_number || "",
        contact_phone: r.contact_phone,
        notes: r.notes || "",
        maps_url: r.maps_url || "",
      });
    } else {
      setForm((prev) => ({
        ...emptyForm(),
        contact_phone: b.registration.phone || prev.contact_phone,
      }));
    }
  };

  useEffect(() => {
    if (!registrationId) return;
    setLoading(true);
    adminGetHotelReservation(registrationId)
      .then(applyBundle)
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error))
      .finally(() => setLoading(false));
  }, [registrationId, t.common.error]);

  const payload = (): ConferenceHotelReservationInput => ({
    ...form,
    room_number: form.room_number?.trim() || null,
    notes: form.notes?.trim() || null,
    maps_url: form.maps_url?.trim() || null,
  });

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!registrationId) return;
    setBusy(true);
    setError(null);
    try {
      const b = await adminUpsertHotelReservation(registrationId, payload());
      applyBundle(b);
      setMessage(t.admin.hotelSaved);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onPreview = async () => {
    if (!registrationId) return;
    setBusy(true);
    setError(null);
    try {
      const p = await adminPreviewHotelReservation(registrationId, payload());
      setPreview(p);
      setMessage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onSend = async () => {
    if (!registrationId) return;
    setBusy(true);
    setError(null);
    try {
      const b = await adminSendHotelReservation(registrationId, payload());
      applyBundle(b);
      setPreview(null);
      setMessage(t.admin.hotelSent);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
      // Reload so failed send history still appears if API persisted it.
      try {
        const b = await adminGetHotelReservation(registrationId);
        applyBundle(b);
      } catch {
        /* ignore */
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p>{t.common.loading}</p>;
  if (!bundle) {
    return (
      <div>
        <PageHeader
          title={t.admin.hotelTitle}
          backTo="/admin/registrations"
          backLabel={t.admin.registrations}
        />
        <div className="dn-alert dn-alert-error">{error || t.common.error}</div>
      </div>
    );
  }

  const reg = bundle.registration;

  return (
    <div>
      <PageHeader
        title={t.admin.hotelTitle}
        lead={t.admin.hotelLead}
        backTo="/admin/registrations"
        backLabel={t.admin.registrations}
      />

      <div className="dn-card" style={{ marginBottom: "1.25rem" }}>
        <h3>{t.admin.hotelRecipient}</h3>
        <p>
          <strong>{reg.full_name}</strong>
          <br />
          {reg.email}
          <br />
          {t.admin.hotelCode}: <code>{reg.registration_code}</code>
          <br />
          {t.admin.hotelLanguage}: {reg.preferred_language.toUpperCase()}
        </p>
        <p className="dn-muted">{t.admin.hotelRecipientFixed}</p>
      </div>

      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}

      {bundle.reservation && (
        <p className="dn-muted" style={{ marginBottom: "1rem" }}>
          {t.admin.hotelStatus}: <strong>{bundle.reservation.status}</strong>
          {bundle.reservation.last_sent_at
            ? ` · ${t.admin.hotelLastSent}: ${formatDateTime(bundle.reservation.last_sent_at, locale)}`
            : ""}
          {bundle.reservation.last_error ? ` · ${bundle.reservation.last_error}` : ""}
        </p>
      )}

      <form className="dn-form" onSubmit={onSave}>
        <label>
          {t.admin.hotelName}
          <input
            required
            value={form.hotel_name}
            onChange={(e) => setForm((f) => ({ ...f, hotel_name: e.target.value }))}
          />
        </label>
        <label>
          {t.admin.hotelAddress}
          <textarea
            required
            rows={2}
            value={form.hotel_address}
            onChange={(e) => setForm((f) => ({ ...f, hotel_address: e.target.value }))}
          />
        </label>
        <div className="dn-publish-grid">
          <label>
            {t.admin.hotelCheckIn}
            <input
              type="date"
              required
              value={form.check_in_date}
              onChange={(e) => setForm((f) => ({ ...f, check_in_date: e.target.value }))}
            />
          </label>
          <label>
            {t.admin.hotelCheckOut}
            <input
              type="date"
              required
              value={form.check_out_date}
              onChange={(e) => setForm((f) => ({ ...f, check_out_date: e.target.value }))}
            />
          </label>
        </div>
        <label>
          {t.admin.hotelBookingRef}
          <input
            required
            value={form.booking_reference}
            onChange={(e) => setForm((f) => ({ ...f, booking_reference: e.target.value }))}
          />
        </label>
        <label>
          {t.admin.hotelRoomType}
          <input
            required
            value={form.room_type}
            onChange={(e) => setForm((f) => ({ ...f, room_type: e.target.value }))}
          />
        </label>
        <label>
          {t.admin.hotelRoomNumber}
          <input
            value={form.room_number || ""}
            onChange={(e) => setForm((f) => ({ ...f, room_number: e.target.value }))}
            placeholder={t.admin.hotelRoomNumberHelp}
          />
        </label>
        <label>
          {t.admin.hotelContactPhone}
          <input
            required
            value={form.contact_phone}
            onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
          />
        </label>
        <label>
          {t.admin.hotelMapsUrl}
          <input
            type="url"
            placeholder="https://maps.google.com/…"
            value={form.maps_url || ""}
            onChange={(e) => setForm((f) => ({ ...f, maps_url: e.target.value }))}
          />
        </label>
        <label>
          {t.admin.hotelNotes}
          <textarea
            rows={4}
            value={form.notes || ""}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </label>

        <div className="dn-cta-row">
          <button type="submit" className="dn-btn dn-btn-ghost" disabled={busy}>
            {t.admin.hotelSave}
          </button>
          <button type="button" className="dn-btn dn-btn-ghost" disabled={busy} onClick={onPreview}>
            {t.admin.hotelPreview}
          </button>
          <button type="button" className="dn-btn dn-btn-primary" disabled={busy} onClick={onSend}>
            {bundle.reservation?.last_sent_at ? t.admin.hotelResend : t.admin.hotelSend}
          </button>
        </div>
      </form>

      {preview && (
        <div className="dn-card" style={{ marginTop: "1.5rem" }}>
          <h3>{t.admin.hotelPreview}</h3>
          <p className="dn-muted">
            {t.admin.hotelTo}: {preview.to_email} · {preview.preferred_language.toUpperCase()}
          </p>
          <p>
            <strong>{preview.subject}</strong>
          </p>
          <iframe
            title={t.admin.hotelPreview}
            srcDoc={preview.html}
            style={{ width: "100%", minHeight: 420, border: "1px solid #ddd", background: "#fff" }}
          />
          <button type="button" className="dn-btn dn-btn-ghost" onClick={() => setPreview(null)}>
            {t.common.close}
          </button>
        </div>
      )}

      {bundle.sends.length > 0 && (
        <div style={{ marginTop: "1.75rem" }}>
          <h2>{t.admin.hotelHistory}</h2>
          <div className="dn-table-wrap">
            <table className="dn-table dn-table-responsive">
              <thead>
                <tr>
                  <th>{t.admin.hotelTo}</th>
                  <th>{t.admin.colStatus}</th>
                  <th>{t.admin.hotelLanguage}</th>
                  <th>{t.admin.remindersSentAt}</th>
                  <th>{t.common.error}</th>
                </tr>
              </thead>
              <tbody>
                {bundle.sends.map((s) => (
                  <tr key={s.id}>
                    <td data-label={t.admin.hotelTo}>{s.email}</td>
                    <td data-label={t.admin.colStatus}>{s.status}</td>
                    <td data-label={t.admin.hotelLanguage}>{s.preferred_language || "—"}</td>
                    <td data-label={t.admin.remindersSentAt}>
                      {s.sent_at ? formatDateTime(s.sent_at, locale) : "—"}
                    </td>
                    <td data-label={t.common.error}>{s.error_message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
